/**
 * AMS — Formatting utilities
 *
 * Vietnamese locale by default. All currency, dates, and meter readings
 * use Vietnamese conventions unless explicitly overridden.
 */

// ────────────────────────────────────────────────────────────────────────────
// CURRENCY
// ────────────────────────────────────────────────────────────────────────────

const VND_FMT = new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0,
});

const VND_PLAIN = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });

/**
 * Format Vietnamese đồng. Example: VND(2450000) → "2.450.000 ₫"
 *
 * @param {number} n
 * @param {{ compact?: boolean, noSymbol?: boolean }} [opts]
 */
export function VND(n, opts = {}) {
  if (n == null || isNaN(n)) return '—';
  if (opts.compact) {
    if (Math.abs(n) >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + ' tỷ₫';
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M₫';
    if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(0) + 'k₫';
  }
  if (opts.noSymbol) return VND_PLAIN.format(n);
  return VND_FMT.format(n).replace(/\u00A0/g, ' ');
}

// ────────────────────────────────────────────────────────────────────────────
// UTILITIES (meters, percentages)
// ────────────────────────────────────────────────────────────────────────────

/** Electric meter reading. Example: kWh(142.5) → "142,5 kWh" */
export function kWh(n, { decimals = 1 } = {}) {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('vi-VN', { maximumFractionDigits: decimals }) + ' kWh';
}

/** Water meter reading. Example: m3(8.2) → "8,2 m³" */
export function m3(n, { decimals = 1 } = {}) {
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString('vi-VN', { maximumFractionDigits: decimals }) + ' m³';
}

/** Percentage. Example: pct(0.124) → "12,4%" or pct(12.4, {fromFraction:false}) → "12,4%" */
export function pct(n, { fromFraction = false, decimals = 1 } = {}) {
  if (n == null || isNaN(n)) return '—';
  const value = fromFraction ? n * 100 : n;
  return value.toLocaleString('vi-VN', { maximumFractionDigits: decimals }) + '%';
}

// ────────────────────────────────────────────────────────────────────────────
// DATES
// ────────────────────────────────────────────────────────────────────────────

const DAY_FMT = new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
const DAY_SHORT_FMT = new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit' });
const TIME_FMT = new Intl.DateTimeFormat('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
const MONTH_FMT = new Intl.DateTimeFormat('vi-VN', { month: 'long', year: 'numeric' });
const WEEKDAY_FMT = new Intl.DateTimeFormat('vi-VN', { weekday: 'long' });

/** "15/05/2026" */
export function formatDate(d) {
  const date = toDate(d);
  return date ? DAY_FMT.format(date) : '—';
}

/** "15/05" — for tight table cells */
export function formatDateShort(d) {
  const date = toDate(d);
  return date ? DAY_SHORT_FMT.format(date) : '—';
}

/** "15/05/2026 14:32" */
export function formatDateTime(d) {
  const date = toDate(d);
  return date ? `${DAY_FMT.format(date)} ${TIME_FMT.format(date)}` : '—';
}

/** "Tháng 5 năm 2026" */
export function formatMonth(d) {
  const date = toDate(d);
  return date ? MONTH_FMT.format(date) : '—';
}

/** "Thứ Năm" */
export function formatWeekday(d) {
  const date = toDate(d);
  return date ? WEEKDAY_FMT.format(date) : '—';
}

/**
 * Relative time in Vietnamese.
 * "2 phút trước" · "Hôm qua" · "Còn 3 ngày" · "Quá hạn 5 ngày"
 */
export function formatRelative(d, { ref = new Date() } = {}) {
  const date = toDate(d);
  if (!date) return '—';
  const diffMs = date - ref;
  const sec = Math.round(diffMs / 1000);
  const min = Math.round(sec / 60);
  const hour = Math.round(min / 60);
  const day = Math.round(hour / 24);
  const past = diffMs < 0;
  const abs = (v) => Math.abs(v);

  if (abs(sec) < 60)   return past ? 'Vừa xong' : 'Sắp tới';
  if (abs(min) < 60)   return past ? `${abs(min)} phút trước` : `Còn ${abs(min)} phút`;
  if (abs(hour) < 24)  return past ? `${abs(hour)} giờ trước` : `Còn ${abs(hour)} giờ`;
  if (abs(day) === 1)  return past ? 'Hôm qua' : 'Ngày mai';
  if (abs(day) < 30)   return past ? `Quá hạn ${abs(day)} ngày` : `Còn ${abs(day)} ngày`;
  return formatDate(date);
}

function toDate(d) {
  if (!d) return null;
  if (d instanceof Date) return isNaN(d) ? null : d;
  const parsed = new Date(d);
  return isNaN(parsed) ? null : parsed;
}

// ────────────────────────────────────────────────────────────────────────────
// VIETNAMESE LUNAR CALENDAR
// ────────────────────────────────────────────────────────────────────────────
//
// Existing repo has lunarUtils.js in renter-portal/src/utils/. Once Phase 02
// extracts it into shared/utils/, this wrapper becomes one-liner. For now,
// expose a stable interface here so consumers don't depend on the legacy file.
//
// Returns { day, month, year, isLeap }.

let _legacyLunar = null;
function getLegacyLunar() {
  if (_legacyLunar) return _legacyLunar;
  try {
    // Resolved at runtime to the existing util once Phase 02 lifts it
    _legacyLunar = require('./lunar.js');
  } catch {
    _legacyLunar = null;
  }
  return _legacyLunar;
}

/**
 * Convert a solar date to lunar.
 * Stub: returns null until Phase 02 wires the real implementation. Callers
 * should handle null by falling back to solar.
 */
export function solarToLunar(d) {
  const lib = getLegacyLunar();
  if (lib && lib.solarToLunar) return lib.solarToLunar(toDate(d));
  return null;
}

/** "15/3 ÂL" — short form for calendar cells */
export function formatLunarShort(d) {
  const l = solarToLunar(d);
  if (!l) return '';
  return `${l.day}/${l.month} ÂL`;
}

// ────────────────────────────────────────────────────────────────────────────
// MISCELLANEOUS
// ────────────────────────────────────────────────────────────────────────────

/** Truncate name for table cells: "Nguyễn Thị Hương" → "Nguyễn T. Hương" */
export function shortName(full, { maxLen = 18 } = {}) {
  if (!full) return '';
  if (full.length <= maxLen) return full;
  const parts = full.trim().split(/\s+/);
  if (parts.length < 3) return full;
  // Shorten middle names to first initial
  return `${parts[0]} ${parts.slice(1, -1).map(p => p[0] + '.').join(' ')} ${parts.at(-1)}`;
}

/** "0901234567" → "0901 234 567" */
export function formatPhone(p) {
  if (!p) return '';
  const digits = String(p).replace(/\D/g, '');
  if (digits.length === 10) return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  if (digits.length === 11) return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  return p;
}

/** Initials for avatar. "Nguyễn Văn An" → "NA" */
export function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts.at(-1)[0]).toUpperCase();
}
